import Alpine from 'alpinejs';
import mask from '@alpinejs/mask';
import QRCode from 'qrcode';
import $ from 'jquery';
import select2 from 'select2';
import Swal from 'sweetalert2';
import { startPageVisits } from './page-visit';

window.$ = window.jQuery = $;
select2(window, $);

/*
 * select2 4.1.0 memanggil `_normalizeItem` tanpa `this` saat mengolah hasil
 * AJAX (`results.map(AjaxAdapter.prototype._normalizeItem)`). Sebagai skrip biasa
 * `this` jatuh ke `window` dan tidak apa-apa, tetapi Vite membundelnya sebagai
 * modul (strict mode) sehingga `this` bernilai undefined, `this.container`
 * melempar TypeError, dan dropdown berhenti di "Searching…". Fungsi aslinya
 * dibungkus agar pemanggilan tanpa `this` diperlakukan seperti perilaku
 * aslinya di luar modul.
 */
const Select2SelectAdapter = $.fn.select2.amd.require('select2/data/select');
const normalizeSelect2Item = Select2SelectAdapter.prototype._normalizeItem;

Select2SelectAdapter.prototype._normalizeItem = function (item) {
    return normalizeSelect2Item.call(this ?? {}, item);
};

window.Swal = Swal;
window.Alpine = Alpine;
Alpine.plugin(mask);
startPageVisits();

/**
 * Menggambar QR code pelacakan kendaraan pada struk kasir sebagai SVG.
 *
 * Dipanggil lewat x-init pada tiap kotak QR. Setelah selesai, elemen memancarkan
 * event `tracking-qr-ready` supaya tombol cetak tahu kapan semua QR sudah siap -
 * mencetak sebelum itu menghasilkan struk dengan kotak QR kosong.
 */
window.renderTrackingQr = async (element, url) => {
    element.innerHTML = await QRCode.toString(url, {
        type: 'svg',
        margin: 1,
        errorCorrectionLevel: 'M',
    });

    element.dispatchEvent(new CustomEvent('tracking-qr-ready', { bubbles: true }));
};

/**
 * Inisialisasi Select2 dengan pencarian AJAX on-demand.
 *
 * Mencegah ribuan <option> dirender langsung di HTML awal. Pilihan diambil
 * secara dinamis saat dropdown dibuka atau diketik oleh pengguna.
 *
 * @param {HTMLElement} element
 * @param {{
 *     url?: string,
 *     placeholder?: string,
 *     allowClear?: boolean,
 *     submitOnChange?: boolean,
 *     extraParams?: Record<string, any>
 * }} config
 */
window.initSelect2Ajax = (element, config = {}) => {
    const $el = window.jQuery(element);
    const url = config.url || $el.data('ajax-url');
    const placeholder = config.placeholder || $el.data('placeholder') || '— Pilih —';
    const allowClear = config.allowClear !== undefined ? config.allowClear : true;
    const submitOnChange = config.submitOnChange ?? false;

    if ($el.hasClass('select2-hidden-accessible')) {
        $el.select2('destroy');
    }

    $el.select2({
        width: '100%',
        placeholder: placeholder,
        allowClear: allowClear,
        ajax: {
            url: url,
            dataType: 'json',
            delay: 250,
            data: function (params) {
                return {
                    search: params.term || '',
                    page: params.page || 1,
                    ...(config.extraParams || {})
                };
            },
            processResults: function (data, params) {
                params.page = params.page || 1;
                return {
                    results: data.results || [],
                    pagination: {
                        more: data.pagination ? data.pagination.more : false
                    }
                };
            },
            cache: true
        }
    });

    if (submitOnChange) {
        $el.off('change.submitForm').on('change.submitForm', () => {
            if (element.form) {
                element.form.requestSubmit();
            }
        });
    }

    return $el;
};

/**
 * Bentangan baris pada halaman laporan rekap.
 *
 * Rincian transaksi tidak ikut dirender bersama halaman karena satu periode
 * bisa berisi ribuan baris; barisnya baru diambil saat memang dibentangkan,
 * lalu disimpan supaya membuka-tutup baris yang sama tidak meminta ulang.
 *
 * @param {{endpoint: string, baseQuery: Record<string, string>, keyParam: string}} config
 */
window.reportRowDetails = (config) => ({
    openKey: null,
    // Dinamai khusus supaya bisa dipakai bersama liveReportFilter,
    // yang punya flag loading sendiri untuk seluruh wilayah hasil.
    loadingRows: false,
    error: '',
    rows: [],
    cache: {},

    isOpen(key) {
        return this.openKey === key;
    },

    async toggle(key) {
        if (this.openKey === key) {
            this.openKey = null;

            return;
        }

        this.openKey = key;
        this.error = '';

        if (this.cache[key]) {
            this.rows = this.cache[key];

            return;
        }

        this.loadingRows = true;
        this.rows = [];

        try {
            // baseQuery boleh berupa fungsi bila filternya bisa berubah tanpa
            // memuat ulang halaman - nilainya dibaca saat rincian diminta.
            const baseQuery = typeof config.baseQuery === 'function' ? config.baseQuery() : config.baseQuery;
            const params = new URLSearchParams({ ...baseQuery, [config.keyParam]: key });
            const response = await fetch(`${config.endpoint}?${params.toString()}`, {
                headers: { Accept: 'application/json' },
            });

            if (!response.ok) {
                throw new Error(response.status);
            }

            const data = await response.json();
            this.cache[key] = data.rows;
            this.rows = data.rows;
        } catch {
            this.error = 'Rincian transaksi gagal dimuat. Coba buka ulang barisnya.';
        } finally {
            this.loadingRows = false;
        }
    },
});

/**
 * Filter laporan yang langsung diterapkan tanpa tombol.
 *
 * Mengambil ulang halaman yang sama dengan filter terbaru, lalu hanya mengganti
 * isi tiap [data-live-region]. Form filter tidak ikut diganti - memakai
 * page-visit berarti seluruh <body> diganti sehingga kursor lepas di tengah
 * mengetik dan tanggal/select2 yang sedang dipilih ikut dirender ulang.
 * Permintaan lama dibatalkan supaya hasil filter sebelumnya tidak menimpa yang
 * baru, dan URL ikut diperbarui agar filter bertahan saat halaman dibuka ulang.
 *
 * Halaman memasang `x-ref="filters"` pada form dan `@submit.prevent="refresh()"`;
 * select2 dengan `submitOnChange` otomatis ikut lewat submit tersebut.
 *
 * @param {{hasFilters?: boolean, filterKeys?: string[]}} options
 */
window.liveReportFilter = (options = {}) => ({
    loading: false,
    request: null,
    hasFilters: options.hasFilters ?? false,

    async refresh() {
        const form = this.$refs.filters;
        const params = new URLSearchParams();

        for (const [key, value] of new FormData(form)) {
            if (typeof value === 'string' && value.trim() !== '') {
                params.append(key, value.trim());
            }
        }

        this.hasFilters = (options.filterKeys ?? []).some((key) => params.has(key));

        const query = params.toString();
        const url = form.action + (query !== '' ? `?${query}` : '');

        this.request?.abort();
        const request = new AbortController();
        this.request = request;
        this.loading = true;

        try {
            const response = await fetch(url, {
                headers: { Accept: 'text/html' },
                signal: request.signal,
            });

            if (! response.ok) {
                throw new Error(String(response.status));
            }

            const page = new DOMParser().parseFromString(await response.text(), 'text/html');

            for (const region of this.$root.querySelectorAll('[data-live-region]')) {
                const incoming = page.querySelector(`[data-live-region="${region.dataset.liveRegion}"]`);

                if (incoming) {
                    region.innerHTML = incoming.innerHTML;
                }
            }

            window.history.replaceState(window.history.state, '', url);
        } catch (error) {
            if (error.name !== 'AbortError') {
                window.Swal?.fire({
                    icon: 'error',
                    title: 'Laporan gagal dimuat ulang',
                    text: 'Periksa koneksi lalu ubah filter sekali lagi.',
                });
            }
        } finally {
            if (this.request === request) {
                this.request = null;
                this.loading = false;
            }
        }
    },
});

Alpine.start();

